import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import { Grid, Box, Typography, Link } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { snackAlertData } from 'utils/constants';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { useSeatGeekService } from 'store/services/seatGeek.service';
import { integrationModalStyles } from 'components/Invoice/styles/styles';
import { Modal, TextField, SnackBarAlert, CircleLoading, Button } from 'shared';

const SeatGeekModal = ({ open, onClose, onSuccess, isConnected }) => {
    const classes = integrationModalStyles();
    const companyId = useSelector((state) => state?.company?.data?.id);
    const { addToken, getToken, updateToken } = useSeatGeekService();

    const [apiToken, setApiToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [buttonMode, setButtonMode] = useState('add');
    const [selectedDate, setSelectedDate] = useState(null);
    const [originalToken, setOriginalToken] = useState('');
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);

    const fetchToken = async () => {
        try {
            const response = await getToken({ companyId });
            const { start_date, api_token: token } = response?.data;
            if (token) {
                setApiToken(token);
                setOriginalToken(token);
                setSelectedDate(dayjs(start_date));
                setButtonMode('update');
            }
        } catch (error) {
            handleErrorAlert(setAlertData, undefined, error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setButtonLoading(true);

            const data = {
                company_id: companyId,
                apiToken: apiToken,
                startDate: selectedDate.format('YYYY-MM-DD'),
            };

            const response = buttonMode === 'add' ? await addToken(data) : await updateToken(data);

            onSuccess(response?.message);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setButtonLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && isConnected) {
            fetchToken();
        }
    }, [companyId]);

    return (
        <Modal
            fullWidth
            open={open}
            title={`${buttonMode === 'update' ? 'Update' : 'Add'} Seat Geek Token`}
            handleClose={() => {
                onClose();
            }}
            actionBtnTitle={<Typography>{buttonMode === 'update' ? 'Update' : 'Add'}</Typography>}
        >
            <Fragment>
                {isLoading ? (
                    <CircleLoading />
                ) : (
                    <>
                        {buttonMode === 'update' && originalToken != apiToken && (
                            <Typography variant="body2" color="error" marginBottom={2}>
                                Warning: Updating your Seat Geek token will prevent fetching data
                                from your previously connected token.
                            </Typography>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            The easiest way to find your SeatGeek API token is by logging into the
                            service you use to upload purchase orders — such as{' '}
                            <strong>1Ticket</strong>, <strong>Cloakify</strong>, or{' '}
                            <strong>Auto Processor</strong>. Usually, under{' '}
                            <strong>Credentials</strong> or <strong>Marketplaces</strong> for each
                            exchange, you’ll find your API token listed there.
                            <br />
                            <br />
                            If you can’t find your token, please email{' '}
                            <Link href="mailto:sellers@seatgeek.com" underline="hover">
                                sellers@seatgeek.com
                            </Link>{' '}
                            with your broker name and request your uploader API token.
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    inlineLabel="API Token"
                                    value={apiToken || ''}
                                    onChange={(e) => setApiToken(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter API Token"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={classes.datePicker}
                                        label="Select Start Date"
                                        value={selectedDate}
                                        onChange={(newValue) => setSelectedDate(newValue)}
                                        disabled={
                                            buttonMode === 'update' && apiToken === originalToken
                                        }
                                        minDate={dayjs().startOf('year')}
                                        maxDate={dayjs().endOf('year')}
                                        renderInput={(params) => (
                                            <TextField {...params} fullWidth sx={{ mb: 3 }} />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                loading={buttonLoading}
                                disabled={!apiToken || !selectedDate || originalToken == apiToken}
                                text={buttonMode === 'update' ? 'Update Token' : 'Add Token'}
                            />
                        </Box>
                    </>
                )}

                <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
            </Fragment>
        </Modal>
    );
};

export default SeatGeekModal;
